import { useEffect, useMemo, useState } from 'react';
import { IChartSeries } from '@mais-retorno-front/models/chart-serie';
import { getBenchmarksSeries, getSeries } from './helpers';
import { useAuth } from '@core/features-core/auth/client';
import { IAssetsIdentifier } from './interface';
import themeVariables from '@mais-retorno-front/theme/theme-variables';
import { usePeriod } from '@core/design-system/period-select';

interface UseAccumulatedMovableWindowProps {
  assetsIdentifiers: IAssetsIdentifier;
  currency?: string;
  period: { startDate: number; endDate: number };
}

export const useAccumulatedMovableWindow = ({
  assetsIdentifiers,
  period,
  currency,
}: UseAccumulatedMovableWindowProps) => {
  const [selectedWindow, setSelectWindow] = useState<string>('12');
  const [fundSeries, setSeries] = useState<IChartSeries[]>([]);
  const [benchmarksSeries, setBenchmarksSeries] = useState<IChartSeries[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { userIsPremium } = useAuth();
  const { calculatedPeriod, calcPeriod, createPeriodDatesFromSeries } =
    usePeriod();

  useEffect(() => {
    if (!assetsIdentifiers || !userIsPremium) return;

    setLoading(true);

    if (Array.isArray(assetsIdentifiers)) {
      Promise.all(
        assetsIdentifiers.map((fund) =>
          getSeries({
            identifier: fund.identifier,
            color: fund.color,
            period,
            window: selectedWindow,
            currency,
          }).then((serie) => setSeries((prev) => [...prev, serie]))
        )
      ).finally(() => setLoading(false));
    } else {
      getSeries({
        identifier: assetsIdentifiers as string,
        color: themeVariables.colors.secondary,
        period,
        window: selectedWindow,
        currency,
      })
        .then((series) => setSeries([series]))
        .finally(() => setLoading(false));
    }
  }, [assetsIdentifiers, currency]);

  useEffect(() => {
    if (!fundSeries?.length) return;

    calcPeriod(
      'otimo',
      createPeriodDatesFromSeries([...fundSeries, ...benchmarksSeries])
    );
  }, [fundSeries, benchmarksSeries]);

  const onBenchmarksChange = async (benchmarks: string[]) => {
    if (!userIsPremium) return;

    if (benchmarks.length === 0) {
      setBenchmarksSeries([]);
      return;
    }

    setLoading(true);

    const benchmarksSeries = await Promise.all(
      benchmarks.map((slug) =>
        getBenchmarksSeries({
          slug,
          period,
          window: selectedWindow,
          currency,
        })
      )
    );

    setBenchmarksSeries(benchmarksSeries);
    setLoading(false);
  };

  const onWindowChange = async (window: string) => {
    if (!userIsPremium) return;

    const assets = Array.isArray(assetsIdentifiers)
      ? assetsIdentifiers
      : [
          {
            identifier: assetsIdentifiers,
            color: themeVariables.colors.secondary,
          },
        ];
    const selectedBenchmarks = benchmarksSeries.map((series) => series.id);

    setLoading(true);

    const fundsSeriesPromise = Promise.all(
      assets.map((fund) =>
        getSeries({
          identifier: fund.identifier,
          color: fund.color,
          period,
          window,
          currency,
        })
      )
    );

    const _benchmarksPromise = Promise.all(
      selectedBenchmarks.map((slug) =>
        getBenchmarksSeries({
          slug,
          period,
          window,
          currency,
        })
      )
    );

    try {
      const [fundsSeries, _benchmarks] = await Promise.all([
        fundsSeriesPromise,
        _benchmarksPromise,
      ]);

      setSelectWindow(window);
      setSeries(fundsSeries);
      setBenchmarksSeries(_benchmarks);
    } catch (error) {
      console.log(error);
    }

    setLoading(false);
  };

  const onPeriodChange = (period: string) => {
    calcPeriod(
      period,
      createPeriodDatesFromSeries([...fundSeries, ...benchmarksSeries])
    );
  };

  return {
    selectedWindow,
    setSelectWindow,
    series: useMemo(
      () => [...fundSeries, ...benchmarksSeries],
      [fundSeries, benchmarksSeries]
    ),
    calculatedPeriod,
    loading,
    onBenchmarksChange,
    onWindowChange,
    onPeriodChange,
  };
};

// export const useBoxPlotMovableWindow = (fundsSlug: IAssetsIdentifier) => {
//   const { userIsPremium } = useUserKeycloakAuthStatus();
//   const [loading, setLoading] = useState<boolean>(false);
//   const [series, setSeries] = useState<IChartSeries[]>([]);
//   const [benchmarksSeries, setBenchmarksSeries] = useState<IChartSeries[]>([]);

//   useEffect(() => {
//     if (!fundsSlug || !userIsPremium) return;

//     if (Array.isArray(fundsSlug)) {
//       Promise.all(
//         fundsSlug.map((fund) =>
//           getFundBoxPlotSeries(fund.id, fund.color).then((series) =>
//             setSeries((prev) => [...prev, series])
//           )
//         )
//       ).finally(() => setLoading(false));
//     } else {
//       getFundBoxPlotSeries(fundsSlug as string, themeVariables.colors.secondary)
//         .then((serie) => setSeries([serie]))
//         .finally(() => setLoading(false));
//     }
//   }, [fundsSlug, userIsPremium]);

//   const onBenchmarksChange = async (benchmarks: string[]) => {
//     if (benchmarks.length === 0) {
//       setBenchmarksSeries([]);
//       return;
//     }

//     setLoading(true);

//     const benchmarksSeries = await Promise.all(
//       benchmarks.map((slug) => getBenchmarkBoxPlotSeries(slug))
//     );

//     setBenchmarksSeries(benchmarksSeries);
//     setLoading(false);
//   };

//   return {
//     onBenchmarksChange,
//     benchmarksSeries,
//     boxPlotSeries: useMemo(
//       () => [...series, ...benchmarksSeries],
//       [series, benchmarksSeries]
//     ),
//     loading,
//   };
// };
